import { dateOf, styles } from "@repo/helpers";
import { Col, Row, Tabs, TabsList, TabsTrigger } from "@repo/ui";
import { useMemo, useState } from "react";

import { useSurveyQuery } from "../../api/repApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { useSegmentContext } from "../../contexts/segmentContext";
import { useUpdateRepData } from "../../helpers/queryHooks";
import { RepStatusEnum } from "../../models/rep";
import { AgentSummary } from "./agentSummary";
import { DeactivationDetails } from "./deactivationDetails";
import { Discipline } from "./discipline";
import { MultiOnboardingRequirementsList } from "./onboardingRequirementsList";
import { PotentialDuplicatesGrid } from "./potentialDuplicatesGrid";
import { RecentEnrollmentsView } from "./recentEnrollments";
import { RelatedRepsGrid } from "./relatedRepsGrid";
import { StatusHistoryGrid } from "./statusHistoryGrid";
import { MultiStatusUpdate } from "./statusUpdate";
import style from "./style.module.scss";
import { Surveys } from "./surveys";

function Summary() {
	const context = useRepContext();
	const rep = context.savedRep;
	const colorClass = useMemo(
		() => (rep.statusEnum === RepStatusEnum.Active ? style.green : rep.statusEnum > 4 ? style.red : style.blue),
		[rep.statusEnum],
	);
	const [value, setValue] = useState<string>(rep.dateLastSale ? "enrollments" : "statusHistory");
	const surveyQuery = useSurveyQuery();

	useUpdateRepData();

	return (
		<section id="AgentSummaryContainer" className={styles("container", style.summaryContainer)}>
			<Row tag="section" id="agent-summary-header">
				<Col md="12">
					<h1>
						{rep.firstName} {rep.lastName} <AgentStatus />
					</h1>
				</Col>
			</Row>
			<MultiOnboardingRequirementsList />
			<Discipline lastDisciplineName={rep.lastDisciplineName} possibleBlacklist={rep.possibleBlacklist} />
			<ContingentExpiration />
			<AgentSummary />
			<MultiStatusUpdate />
			<DeactivationDetails
				active={rep.active}
				deactivationDate={rep.derivedDeactivationDate}
				deactivationReason={rep.deactivationReason}
				statusEnum={rep.statusEnum}
			/>
			<Tabs id="repTabs" value={value} onValueChange={setValue}>
				<TabsList>
					<TabsTrigger value="enrollments" hidden={!rep.dateLastSale}>
						Recent Enrollments
					</TabsTrigger>
					<TabsTrigger value="statusHistory">Status History</TabsTrigger>
					<TabsTrigger value="relatedReps">Related Reps</TabsTrigger>
					<TabsTrigger value="potDuplicateReps" hidden={!rep.systemId}>
						Potential Duplicates
					</TabsTrigger>
					<TabsTrigger value="surveys" hidden={!surveyQuery.data?.length}>
						Recent Surveys
					</TabsTrigger>
				</TabsList>
				{value === "enrollments" && <RecentEnrollmentsView />}
				{value === "statusHistory" && <StatusHistoryGrid />}
				{value === "relatedReps" && <RelatedRepsGrid />}
				{value === "potDuplicateReps" && <PotentialDuplicatesGrid />}
				{value === "surveys" && <Surveys />}
			</Tabs>
		</section>
	);

	function AgentStatus() {
		const { segment } = useSegmentContext();
		if (!segment) return <AgentStatusDisplay {...rep} />;

		const overrideStatuses = [RepStatusEnum.Deactivated, RepStatusEnum.OnHold, RepStatusEnum.Terminated];
		if (overrideStatuses.includes(rep.statusEnum))
			return (
				<>
					<AgentStatusDisplay {...segment} statusEnum={segment.statusId} />
					&nbsp;(Agent:
					<AgentStatusDisplay {...rep} />)
				</>
			);
		return <AgentStatusDisplay {...segment} statusEnum={segment.statusId} />;
	}

	function AgentStatusDisplay(props: { statusName: string; statusEnum: RepStatusEnum; statusDetails?: string }) {
		const { statusName, statusEnum, statusDetails } = props;
		let agentColorClass = statusEnum === RepStatusEnum.Active ? style.green : style.blue;

		if (statusEnum === RepStatusEnum.Deactivated || statusEnum == RepStatusEnum.Terminated)
			agentColorClass = style.red;

		if (statusDetails) {
			return (
				<span className={agentColorClass}>
					&nbsp;{statusName}&nbsp;({statusDetails})
				</span>
			);
		}

		return <span className={agentColorClass}>&nbsp;{statusName}</span>;
	}

	function ContingentExpiration() {
		if (!rep.contingentExpirationDate) return null;
		return (
			<Row tag="section">
				<Col md="12">
					<h3 className={colorClass}>Contingent Expiration Date: {dateOf(rep.contingentExpirationDate)}</h3>
				</Col>
			</Row>
		);
	}
}

export default Summary;
